import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import {
  AppEnv,
  BuildEnvironment,
  FrontendAppConfig,
  FrontendAuthConfig,
  FrontendBackendConfig,
  FrontendLogosConfig,
  FrontendResolvedBackendConfig,
  FrontendToolbarConfig,
  FrontendUiConfig,
} from '../config.token';

type RawConfig = {
  app?: Partial<FrontendAppConfig>;
  backend?: Omit<Partial<FrontendBackendConfig>, 'auth'> & { auth?: Partial<FrontendAuthConfig> };
  ui?: {
    logos?: Partial<FrontendLogosConfig>;
    toolbar?: Partial<FrontendToolbarConfig>;
  };
  services?: Record<string, { baseUrl?: string }>;
  security?: { encryptionKey?: string };
};

@Injectable({ providedIn: 'root' })
export class ConfigService {
  private readonly http = inject(HttpClient);
  private static buildEnvironment: BuildEnvironment = { production: false };
  private static loadPromise: Promise<void> | null = null;
  private static startupError: string | null = null;

  static settings: AppEnv = ConfigService.emptySettings();

  async load(buildEnvironment: BuildEnvironment = { production: false }): Promise<void> {
    if (ConfigService.loadPromise) {
      return ConfigService.loadPromise;
    }

    ConfigService.startupError = null;
    ConfigService.loadPromise = this.loadInternal(buildEnvironment);

    return ConfigService.loadPromise;
  }

  private async loadInternal(buildEnvironment: BuildEnvironment): Promise<void> {
    ConfigService.buildEnvironment = buildEnvironment;

    const [config, info] = await Promise.all([
      this.loadJson<RawConfig>('assets/config/config.json', {}),
      this.loadJson<Record<string, any>>('assets/info.json', {}),
    ]);

    const backend = this.readBackend(config);
    let publicAuthConfig: PublicAuthConfigResponse = {
      auth: { sessionTimeoutMs: 0 },
    };
    
    const app = this.readApp(config);

    if (app.id !== 'gp'){
      try {
        publicAuthConfig = await this.loadPublicAuthConfig(backend.publicConfigUrl);
      } catch (error) {
        ConfigService.startupError =
          'Não foi possível conectar ao serviço. Tente novamente mais tarde. Se o problema persistir, entre em contato com o suporte.';
        console.error('Falha ao carregar a configuração pública de autenticação.', error);
      }
    }

    ConfigService.settings = {
      app: app,
      backend,
      ui: this.readUi(config),
      auth: this.readAuth(publicAuthConfig),
      services: Object.fromEntries(
        Object.entries(config.services ?? {}).map(([name, value]) => [name, { baseUrl: value?.baseUrl ?? '' }]),
      ),
      security: { ...(config.security ?? {}) },
      info,
    };
  }

  getApiUrl(name: string): string {
    return ConfigService.getApiUrl(name);
  }

  getApp(): string {
    return ConfigService.settings.app.id;
  }

  static getApiUrl(name: string): string {
    const backend = ConfigService.settings.backend;

    switch (name) {
      case 'baseUrl': return backend.baseUrl;
      case 'integrateUrl': return backend.integrateUrl;
      case 'publicConfigUrl': return backend.publicConfigUrl;
      case 'parametersUrl': return backend.parametersUrl ?? '';
      case 'authBaseUrl': return backend.auth.baseUrl ?? '';
      case 'loginUrl': return backend.auth.loginUrl;
      case 'changePasswordUrl': return backend.auth.changePasswordUrl;
      case 'passwordResetAdminRequestUrl': return backend.auth.passwordResetAdminRequestUrl ?? '';
      case 'passwordResetSelfRequestUrl': return backend.auth.passwordResetSelfRequestUrl ?? '';
      case 'passwordResetConfirmUrl': return backend.auth.passwordResetConfirmUrl ?? '';
      case 'passwordResetPolicyUrl': return backend.auth.passwordResetPolicyUrl ?? '';
      case 'firstLoginUrl': return backend.auth.firstLoginUrl ?? '';
      case 'refreshUrl': return backend.auth.refreshUrl ?? '';
      case 'logoutUrl': return backend.auth.logoutUrl ?? '';
      default: return '';
    }
  }

  static consumeStartupError(): string | null {
    const error = ConfigService.startupError;
    ConfigService.startupError = null;
    return error;
  }

  static hasStartupError(): boolean {
    return ConfigService.startupError !== null;
  }

  getServiceUrl(name: string): string {
    return ConfigService.getServiceUrl(name);
  }

  static getServiceUrl(serviceName: string): string {
    return ConfigService.settings.services[serviceName]?.baseUrl ?? '';
  }

  getSecurityKey(): string {
    return ConfigService.getSecurityKey();
  }

  static getSecurityKey(): string {
    return ConfigService.settings.security?.encryptionKey ?? '';
  }

  getInfo(name: string): any {
    return ConfigService.settings.info[name];
  }

  isProd(): boolean {
    return ConfigService.buildEnvironment.production === true;
  }

  private async loadJson<T>(url: string, fallback: T): Promise<T> {
    try {
      return await lastValueFrom(this.http.get<T>(url));
    } catch (error) {
      console.warn(`Configuração opcional não encontrada em ${url}; usando fallback.`, error);
      return fallback;
    }
  }

  private readApp(config: RawConfig): FrontendAppConfig {
    return {
      id: config.app?.id ?? '',
      service: config.app?.service ?? false,
      environmentName: config.app?.environmentName ?? '',
      environmentDescription: config.app?.environmentDescription ?? '',
    };
  }

  private readBackend(config: RawConfig): FrontendResolvedBackendConfig {
    const source = config.backend ?? {};
    const auth = source.auth ?? {};
    const baseUrl = source.baseUrl ?? '';
    const publicConfigPath = source.publicConfigPath ?? '';

    return {
      baseUrl,
      integrateUrl: this.resolveUrl(baseUrl, source.integratePath ?? 'integrate'),
      publicConfigUrl: publicConfigPath ? this.resolveUrl(baseUrl, publicConfigPath) : '',
      parametersUrl: source.parametersPath ? this.resolveUrl(baseUrl, source.parametersPath) : undefined,
      auth: {
        baseUrl: this.resolveUrl(baseUrl, 'auth'),
        loginUrl: this.resolveUrl(baseUrl, auth.loginPath ?? 'auth/login'),
        changePasswordUrl: this.resolveUrl(baseUrl, auth.changePasswordPath ?? 'change-password'),
        passwordResetAdminRequestUrl: this.resolveUrl(
          baseUrl,
          auth.passwordResetAdminRequestPath ?? 'auth/password-reset/admin-request',
        ),
        passwordResetSelfRequestUrl: this.resolveUrl(
          baseUrl,
          auth.passwordResetSelfRequestPath ?? 'auth/password-reset/self-request',
        ),
        passwordResetConfirmUrl: this.resolveUrl(
          baseUrl,
          auth.passwordResetConfirmPath ?? 'auth/password-reset/confirm',
        ),
        passwordResetPolicyUrl: this.resolveUrl(
          baseUrl,
          auth.passwordResetPolicyPath ?? 'auth/password-reset/password-policy',
        ),
        firstLoginUrl: auth.firstLoginPath ? this.resolveUrl(baseUrl, auth.firstLoginPath) : undefined,
        refreshUrl: auth.refreshPath ? this.resolveUrl(baseUrl, auth.refreshPath) : undefined,
        logoutUrl: auth.logoutPath ? this.resolveUrl(baseUrl, auth.logoutPath) : undefined,
      },
    };
  }

  private readUi(config: RawConfig): FrontendUiConfig {
    const logos = config.ui?.logos ?? {};
    const toolbar = config.ui?.toolbar ?? {};

    return {
      logos: {
        customer: logos.customer ?? '',
        i7Code: logos.i7Code ?? '',
        login: logos.login,
        report: logos.report,
      },
      toolbar: {
        backgroundColor: toolbar.backgroundColor ?? '',
        color: toolbar.color ?? '',
        height: toolbar.height ?? '',
      },
    };
  }

  private readAuth(config: PublicAuthConfigResponse): AppEnv['auth'] {
    return {
      sessionTimeoutMs: config.auth.sessionTimeoutMs,
    };
  }

  private async loadPublicAuthConfig(publicConfigUrl: string): Promise<PublicAuthConfigResponse> {
    if (!publicConfigUrl) {
      throw new Error('Não foi possível resolver a URL da configuração pública de autenticação.');
    }

    const response = await lastValueFrom(this.http.get<unknown>(publicConfigUrl));
    this.validatePublicAuthConfig(response);
    return response;
  }

  private validatePublicAuthConfig(value: unknown): asserts value is PublicAuthConfigResponse {
    if (!value || typeof value !== 'object') {
      throw new Error('A configuração pública de autenticação possui formato inválido.');
    }

    const auth = (value as { auth?: unknown }).auth;
    if (!auth || typeof auth !== 'object') {
      throw new Error('A configuração pública não possui o bloco auth.');
    }

    const authConfig = auth as Partial<PublicAuthConfig['auth']>;
    if (
      typeof authConfig.sessionTimeoutMs !== 'number' ||
      !Number.isFinite(authConfig.sessionTimeoutMs) ||
      authConfig.sessionTimeoutMs <= 0
    ) {
      throw new Error('A configuração pública de autenticação possui valores inválidos.');
    }
  }

  private resolveUrl(baseUrl: string, path: string): string {
    if (!baseUrl) return '';
    if (/^https?:\/\//i.test(path)) return path;
    if (path.startsWith('/')) {
      return new URL(path, baseUrl).toString();
    }
    return `${baseUrl.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`;
  }

  private static emptySettings(): AppEnv {
    return {
      app: { id: '', service: false, environmentName: '', environmentDescription: '' },
      backend: {
        baseUrl: '',
        integrateUrl: '',
        publicConfigUrl: '',
        auth: {
          baseUrl: '',
          loginUrl: '',
          changePasswordUrl: '',
          passwordResetAdminRequestUrl: '',
          passwordResetSelfRequestUrl: '',
          passwordResetConfirmUrl: '',
          passwordResetPolicyUrl: '',
        },
      },
      ui: {
        logos: { customer: '', i7Code: '' },
        toolbar: { backgroundColor: '', color: '', height: '' },
      },
      services: {},
      security: {},
      info: {},
      auth: {
        sessionTimeoutMs: 0,
      },
    };
  }
}

type PublicAuthConfig = {
  auth: {
    sessionTimeoutMs: number;
  };
};

type PublicAuthConfigResponse = PublicAuthConfig;
